import axios from 'axios';
import {detect} from 'detect-browser';
import api from 'services/api';
import * as Types from './types';
import {getIntegrationName} from '../utils';

export const login = (email, password) => async dispatch => {
    dispatch({ type:Types.AUTHENTICATING });
    try {
        const token = await api.login(email, password);
        const user = await api.http({ url: `/clients/${token.clientID}/users/${token.userID}`, method:'get' });
        const client = await api.http({ url: `/clients/${token.clientID}`, method:'get' });
        dispatch({ type: Types.LOGIN, payload: { ...token, userName: user.fullName, clientName: client.name }});
    } catch (error) {
        if(error.status === 401) dispatch({ type: Types.AUTH_ERROR });
        else dispatch({ type: Types.LOGIN_ERROR });
        throw error;
    }
}

export function authQueryLogin(auth){
    api.setToken(auth.ID);
    return {
        type:Types.LOGIN,
        payload:auth
    }
}

export function fetchUser(){
    return (dispatch,getState) => {
        const {clientID,ID} = getState().user;
        return api.http({
            url:`/clients/${clientID}/users/${ID}`,
            method:'get'
        })
        .catch((error) => {
            if(error.status === 401){
                dispatch(logout());
            }
            throw error;
        })
    }
}

export function validateToken(){
    return (dispatch,getState) => {
        const {token} = getState().auth;
        const {clientID,ID} = getState().user;
        if(!token || !clientID || !ID){
            dispatch({
                type:Types.LOGOUT
            });
        }
        //Set token if needed
        api.setToken(token);
        return api.validateToken(clientID,ID,token)
        .then(user=>{
            dispatch({
                type:Types.FETCH_USER,
                payload:user
            });
        })
        .catch(() => {
            dispatch(logout());
        })
    }
}

export function logout(){
    api.logout();
    return{
        type:Types.LOGOUT
    };
}

export const fetchApps = (authenticated) => async (dispatch,getState) => {
    dispatch({
        type:Types.LOADING_APPS,
        payload:authenticated
    });
    const {clientID, ID} = getState().user;
    try{
        const response = await Promise.all([
            api.http({
                url:!clientID && !ID ? `/apps` : `/clients/${clientID}/users/${ID}/apps`,
                method:'get'
            }),
            api.http({
                url:'/kvpgroups/images', 
                method:'get'
            })
        ]);
        const apps = response[0].data.map(app => {
            return {...app,logoGraphic:response[1].kvps.integrations[getIntegrationName(app.source)].image}
        })
        dispatch({
            type:Types.FETCH_APPS, 
            payload:{
                apps:apps
            }
        });
    } catch(error) {
        if(error.status === 401) dispatch(logout());
        throw error;
    }
}

export function doReset(app){
    return (dispatch,getState) => {
        const {clientID,ID} = getState().user;
        dispatch({
            type:Types.APP_RESETTING,
            payload:app.appInstallationID
        });
        return api.http({
            url:`/clients/${clientID}/users/${ID}/apps/${app.appInstallationID}`,
            method:'put',
            data:{tokenInvalid:false}
        })
        .then(()=>{
            dispatch(getAuthUrl(app));
        })
        .catch((error) => {
            if(error.status === 401){
                dispatch(logout());
            }
            throw error;
        })
    }
}

export const getAuthUrl = (app) => dispatch =>{
    const {ID,oauthURL,oAuthVersion} = app;
    dispatch(clearInstallAppDetails());
    if(oAuthVersion==='1.0a'){
        const config = { 
            method: 'post',
            url: `https://integrations-auth.circleloop.com/auth`,
            data: {ID}
        };
        return axios(config)
        .then(response=>{
            if (response.data) {
                dispatch({
                    type: Types.GET_URL,
                    payload: response.data.secret
                })
                if (response.data.authURL) window.location = response.data.authURL;
            }
        })
        .catch((error) => {
            if(error.status === 401){
                dispatch(logout());
            }
            throw error;
        })
        
    } else {
        window.location = oauthURL;
    }
}
 
export function setRedirectTo(url){
    return{
        type:Types.SET_REDIRECT_TO,
        payload:url
    };
}

export function openConfig(appID){
    return{
        type:Types.CONFIG_OPEN,
        payload:appID
    };
}

export function closeConfig(appID){
    return{
        type:Types.CONFIG_CLOSE,
        payload:appID
    };
}

export function saveSettings(appID,settings){
    return (dispatch,getState) => {
        const {clientID,ID} = getState().user;

        dispatch({
            type:Types.SETTINGS_SAVING,
            payload:appID
        });

        return api.http({
            url:`/clients/${clientID}/users/${ID}/apps/${appID}/settings`,
            method:'post',
            data:settings
        })
        .then(() => {
            dispatch({
                type:Types.SETTINGS_SAVED,
                payload:{appID: appID, settings: settings}
            });
        })
        .catch((error) => {
            if(error.status === 401){
                dispatch(logout());
            }
            throw error;
        })
    }
}

export const installApp = (appRecord, form) => async (dispatch, getState) => {
    const { clientID,ID } = getState().user;

    if(form.isCustomOAuth && appRecord.initialAuthURL){
        try {
            const app = await api.http({
                url:`/clients/${clientID}/users/${ID}/apps/${appRecord.appInstallationID}/settings`, // post at the settings to perform custom tasks without modifying the integrations side appinstall
                method:'post',
                data:form
            });
            dispatch({
                type:Types.SETTINGS_SAVED,
                payload:{appID: appRecord.appInstallationID, settings: form}
            });

            if(appRecord.finalRedirect){
                window.location = appRecord.finalRedirect;
                return;
            }else{
                if(appRecord.postInstall || appRecord.promptExtension) dispatch(postInstallRequired(appRecord))
                return;
            }

        } catch (error) {
            if(error.status === 401) dispatch(logout());
            throw error;
        }
    }
    if(appRecord.initialAuthURL && form.oAuthDomain && form.oAuthClientID && form.oAuthAppSecret){ // handle unique oauth
        try {
            const app = await api.http({
                url:`/clients/${clientID}/users/${ID}/apps`,
                method:'post',
                data:{ appID: appRecord.ID, authResponse: form }
            });
            const created = Date.parse(app.created)/1000;
            dispatch({
                type:Types.APP_INSTALLED,
                payload:{...app, appInstallationDate:created, appInstallationID:app.ID}
            });

            const authURL = appRecord.initialAuthURL.replace(/APPID/, form.oAuthClientID);
            window.location = authURL;
            return;
        } catch (error) {
            if(error.status === 401) dispatch(logout());
            throw error;
        }
    }
    if(form.subDomain){ // handle shopify
        const authURL = appRecord.oauthURL.replace(/SUBDOMAIN/, form.subDomain);
        window.location = authURL;
        return;
    }
    try {
        const app = await api.http({
            url:`/clients/${clientID}/users/${ID}/apps`,
            method:'post',
            data:{ appID: appRecord.ID, authResponse: form }
        });
        const created = Date.parse(app.created)/1000;
        dispatch({
            type:Types.APP_INSTALLED, 
            payload:{...app, appInstallationDate:created, appInstallationID:app.ID}
        });
        if(appRecord.postInstall || appRecord.promptExtension) dispatch(postInstallRequired(appRecord))
    } catch (error) {
        if(error.status === 401) dispatch(logout());
        throw error;
    }
}

const detectBrowser = (app) => {
    if(app.promptExtension){
        const browser = detect();
        let userBrowser = browser && browser.name;
        return userBrowser ? userBrowser : null;
    }
}

const postInstallRequired = (app) => {
    return {
        type: Types.POST_INSTALL_REQUIRED,
        payload: { 
            browser: detectBrowser(app),
            config: app.postInstall,
            appID: app.ID
        }
    }
}

export function uninstallApp(appID, resetApp){
    return (dispatch,getState) => {
        const {clientID,ID} = getState().user;
        dispatch({
            type:Types[resetApp !== undefined ? 'APP_RESETTING' : 'APP_UNINSTALLING'],
            payload:appID
        });
        return api.http({
            url:`/clients/${clientID}/users/${ID}/apps/${appID}`,
            method:'delete'
        })
        .then(()=>{
            dispatch({
                type:Types.APP_UNINSTALLED,
                payload:appID
            });
            dispatch({
                type:Types.CONFIG_CLOSE,
                payload:appID
            });
            dispatch(fetchApps());
        })
        .catch((error) => {
            if(error.status === 401){
                dispatch(logout());
            }
            throw error;
        })
    }
}

export const migrateApp = (app) => async (dispatch, getState) => {
    const { appInstallationID, migrateTo } = app;
    try {
        await dispatch(uninstallApp(appInstallationID, 'resetApp'));
        const appToMigrateTo = getState().apps.find(app => app.ID === migrateTo);
        if(appToMigrateTo.preInstall) dispatch(toggleFormModal(migrateTo, 'install'));
        else dispatch(getAuthUrl(appToMigrateTo));
    } catch (error) {
        if(error.status === 401){
            dispatch(logout());
        }
        throw error;
    }
}

export function resetPassword(email){
    return (dispatch) => {
        dispatch({
            type:Types.RESETTING_PASSWORD
        })
        return api.http({
            url:'/resettokens',
            method:'post',
            data:{
                email:email
            }
        })
        .then(token => {
            dispatch({
                type:Types.PASSWORD_RESET
            });
        })
        .catch(error => {
            dispatch({
                type:Types.RESET_ERROR
            })
        })
    }
}

export function lookUpAuthApp(app){
    return (dispatch)=>{
        dispatch({
            type:Types.LOOKING_UP_AUTH_APP
        })
        return api.http({
            url:`/oauth/apps/${app.client_id}`,
            method:'get'
        })
        .then(response=>{
            const validUri = response.redirectUris.includes(app.redirect_uri);
            const validResponse = app.response_type==='code';
            if(validUri && validResponse){
                dispatch({
                    type:Types.AUTH_APP_EXISTS,
                    payload:response
                });
            }
            else if(validUri && !validResponse){
                throw new Error({message:'Invalid response'});
            }
            else throw new Error({message:'Invalid redirect_uri'});
        })
        .catch(err=>{
            if(err.message===`Oauth with ID ${app.client_id} not found.`){
                dispatch(authAppError('Invalid client_id'));
                throw err;
            }
            if(err.message==='Invalid redirect_uri'){
                dispatch(authAppError('Invalid redirect_uri'));
                throw err;
            }
            if(err.message==='Invalid response'){
                window.location = `${app.redirect_uri}?error=unsupported_response_type&state=${app.state}`;
                throw err;
            }
        })
    }
}

export function authAppError(error){
    return{
        type:Types.AUTH_APP_ERROR,
        payload:error
    }
}

export function authoriseApp(query){
    return ()=>{
        return api.http({
            url:`/oauth/authorise?client_id=${query.client_id}&redirect_uri=${query.redirect_uri}`,
            method:'get'
        })
        .then(response=>{
            window.location = `${query.redirect_uri}?code=${response.code}&state=${query.state}`;
        })
        .catch(()=>{
            window.location = `${query.redirect_uri}?error=server_error&state=${query.state}`;
        })
    }
}

export const csvUpload = (file,integrationName) => async (dispatch,getState) => {
    const {clientID,ID} = getState().user;
    try {
        const response = await api.http({
            url:`/clients/${clientID}/users/${ID}/files?type=contacts&source=${integrationName}`,
            method:'post',
            data:file
        });
        dispatch({
            type: Types.UPLOAD_CONTACTS_CSV,
            payload: response
        });
    } catch (error) {
        if(error.status === 401) dispatch(logout());
        throw error;
    }
}

export const saveInstallAppDetailsUntilLogin = (ID,apiKey) => {
    return {
        type:Types.SAVE_INSTALL_APP_DETAILS_UNTIL_LOGIN,
        payload:{appIDToInstall:ID,apiKey:apiKey === 'requireApiKey'} 
    }
}

export const clearInstallAppDetails = () => {
    return {
        type:Types.CLEAR_INSTALL_APP_DETAILS
    }
}

export const toggleFormModal = (appID,form) => {
    return {
        type: Types.TOGGLE_FORM_MODAL,
        payload: {appID: appID, form: form}
    }
}


// WEBPACK FOOTER //
// ./src/actions/index.js